// apps/web/src/api/client.ts
// Zustand store, handles the 401 silent-refresh protocol (single retry with
// stale-token detection and skipAuthRetry escape hatch), and normalises
// errors via `parseApiError` / `NetworkError` / `ApiClientError`.
import type { ApiClient, QueryValue, RequestOptions, ResponseParser } from '@magimp/shared-hooks';
import type { ZodType, ZodTypeDef } from 'zod';
import { useAuthStore } from '../stores/auth.js';
import { ApiClientError, NetworkError, parseApiError } from './errors.js';
import { coordinatedRefresh } from './refresh.js';
import { clearWithBroadcast, type SessionLostReason } from './authEvents.js';

// WHY: the api-client is the single point that observes an authoritative
// 4xx response from `/api/auth/refresh` (the coordinator throws, we catch).
// Mapping the ErrorCode → broadcast reason here lets sister tabs converge
// to logged-out state with a useful diagnostic without the coordinator
// having to know about the cross-tab event channel.
function refreshErrorReason(err: ApiClientError): SessionLostReason {
  if (err.code === 'AUTH_REFRESH_INVALID') return 'AUTH_REFRESH_INVALID';
  if (err.code === 'AUTH_REFRESH_REUSED') return 'AUTH_REFRESH_REUSED';
  return 'AUTH_REFRESH_4XX';
}

// is applied here (single source) so call-sites in shared-hooks remain
// path-only ('/users', '/auth/login', …) and stay reusable by future
// clients (e.g. mobile) that may pick a different prefix.
const API_PREFIX = '/api';

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const prefixed = API_PREFIX + path;
  if (!query) return prefixed;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined) continue;
    params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${prefixed}?${qs}` : prefixed;
}

// WHY: overloads expose two narrow contracts to callers — schema-present
// (T inferred from the parser) and schema-absent (T comes from the generic).
// The implementation signature is widened to Promise<unknown> so the body
// returns raw JSON, parsed JSON, or undefined (for 204) without a type
// assertion escaping out of the implementation.
export function request<T>(
  path: string,
  init: RequestInit,
  opts: RequestOptions,
  schema: ZodType<T, ZodTypeDef, unknown>,
): Promise<T>;
export function request<T>(path: string, init: RequestInit, opts?: RequestOptions): Promise<T>;
export async function request(
  path: string,
  init: RequestInit,
  opts: RequestOptions = {},
  schema?: ZodType<unknown>,
): Promise<unknown> {
  const url = buildUrl(path, opts.query);

  // Capture the token at request start. If a concurrent refresh rotates
  // the store BEFORE our 401 arrives, we'll detect it below and just retry.
  const tokenAtStart = useAuthStore.getState().accessToken;
  const headers = new Headers(init.headers);
  if (opts.headers) for (const [k, v] of Object.entries(opts.headers)) headers.set(k, v);
  if (tokenAtStart) headers.set('authorization', `Bearer ${tokenAtStart}`);

  const baseInit: RequestInit = { ...init, headers, credentials: 'include' };
  if (opts.signal) baseInit.signal = opts.signal;

  let res: Response;
  try {
    res = await fetch(url, baseInit);
  } catch (e) {
    throw new NetworkError('Network request failed', { cause: e });
  }

  if (res.status === 401 && tokenAtStart) {
    // ── skipAuthRetry branch ──────────────────────────────────────────
    // Callers that have JUST minted an access token (e.g. useAuthBootstrap
    // calling /auth/me right after /auth/refresh) pass skipAuthRetry:true.
    // On a 401 here we do NOT call coordinatedRefresh — re-refreshing
    // immediately would burn a second rotation for no benefit (the 401 is
    // from clock skew, mid-flight deactivation, etc.). We also do NOT
    // clear() the store — the store already holds the fresh access token;
    // letting the next real request's 401 trigger recovery is correct.
    if (opts.skipAuthRetry) {
      throw await parseApiError(res);
    }

    const tokenNow = useAuthStore.getState().accessToken;
    if (tokenNow !== tokenAtStart && tokenNow != null) {
      // Stale 401: another request already refreshed. Retry with the new token.
      const retryHeaders = new Headers(init.headers);
      if (opts.headers) for (const [k, v] of Object.entries(opts.headers)) retryHeaders.set(k, v);
      retryHeaders.set('authorization', `Bearer ${tokenNow}`);
      const retryInit: RequestInit = { ...init, headers: retryHeaders, credentials: 'include' };
      if (opts.signal) retryInit.signal = opts.signal;
      try {
        res = await fetch(url, retryInit);
      } catch (e) {
        throw new NetworkError('Network request failed', { cause: e });
      }
    } else {
      try {
        const newToken = await coordinatedRefresh();
        const retryHeaders = new Headers(init.headers);
        if (opts.headers) for (const [k, v] of Object.entries(opts.headers)) retryHeaders.set(k, v);
        retryHeaders.set('authorization', `Bearer ${newToken}`);
        const retryInit: RequestInit = { ...init, headers: retryHeaders, credentials: 'include' };
        if (opts.signal) retryInit.signal = opts.signal;
        try {
          res = await fetch(url, retryInit);
        } catch (e) {
          throw new NetworkError('Network request failed', { cause: e });
        }
      } catch (e) {
        if (e instanceof NetworkError) {
          // Transient: preserve the session; surface a retryable error to caller.
          throw e;
        }
        // Authoritative auth loss (4xx from /auth/refresh). Route through
        // the cross-tab session-clear path so sister tabs in the same
        // browser profile converge without each firing their own refresh.
        const reason = e instanceof ApiClientError ? refreshErrorReason(e) : 'AUTH_REFRESH_4XX';
        clearWithBroadcast(reason);
        throw new ApiClientError(401, 'AUTH_TOKEN_INVALID', 'Session expired');
      }
    }
  }

  if (!res.ok) throw await parseApiError(res);
  // WHY: a schema-bearing call must not bypass validation when the server
  // unexpectedly answers 204 — feed `undefined` to the parser so z.void()
  // still accepts and z.object(...)/array/etc reject with a ZodError, the
  // same failure mode as a JSON-shape mismatch.
  if (res.status === 204) return schema ? schema.parse(undefined) : undefined;
  const body = await res.json();
  if (schema) return schema.parse(body);
  return body;
}

function jsonInit(method: string, body?: unknown): RequestInit {
  const init: RequestInit = { method };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers = { 'content-type': 'application/json' };
  }
  return init;
}

/**
 * @aiPurpose binary download companion to `request<T>`. The auth-retry
 *   semantics (stale-token detection, single coordinatedRefresh + retry,
 *   credentials, AbortSignal forwarding, NetworkError/ApiClientError
 *   shaping) MUST stay in lock-step with `request` — drift opens a hole
 *   where the JSON path silently refreshes but a blob download fails on
 *   a stale 401. The duplication is intentional: the JSON branch parses
 *   `res.json()` on success while this branch returns the raw response
 *   (the caller pulls the Blob and Content-Disposition filename).
 * @aiInvariant on non-2xx the response body is parsed via the standard
 *   JSON envelope through `parseApiError` — the export endpoint and any
 *   other binary route MUST keep returning the JSON envelope on errors.
 */
interface BinaryRequestInput {
  method?: 'GET' | 'POST';
  jsonBody?: unknown;
}

function binaryRequestInit(
  input: BinaryRequestInput,
  opts: RequestOptions,
  token: string | null,
): RequestInit {
  const headers = new Headers();
  if (opts.headers)
    for (const [key, value] of Object.entries(opts.headers)) headers.set(key, value);
  if (token) headers.set('authorization', `Bearer ${token}`);
  const init: RequestInit = {
    method: input.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (input.jsonBody !== undefined) {
    headers.set('content-type', 'application/json');
    init.body = JSON.stringify(input.jsonBody);
  }
  if (opts.signal) init.signal = opts.signal;
  return init;
}

async function fetchBinaryWithAuthRetry(
  path: string,
  opts: RequestOptions,
  input: BinaryRequestInput,
): Promise<Response> {
  const url = buildUrl(path, opts.query);
  const tokenAtStart = useAuthStore.getState().accessToken;
  const init = binaryRequestInit(input, opts, tokenAtStart);

  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    throw new NetworkError('Network request failed', { cause: e });
  }

  if (res.status === 401 && tokenAtStart) {
    if (opts.skipAuthRetry) {
      throw await parseApiError(res);
    }
    const tokenNow = useAuthStore.getState().accessToken;
    if (tokenNow !== tokenAtStart && tokenNow != null) {
      const retryInit = binaryRequestInit(input, opts, tokenNow);
      try {
        res = await fetch(url, retryInit);
      } catch (e) {
        throw new NetworkError('Network request failed', { cause: e });
      }
    } else {
      try {
        const newToken = await coordinatedRefresh();
        const retryInit = binaryRequestInit(input, opts, newToken);
        try {
          res = await fetch(url, retryInit);
        } catch (e) {
          throw new NetworkError('Network request failed', { cause: e });
        }
      } catch (e) {
        if (e instanceof NetworkError) throw e;
        const reason = e instanceof ApiClientError ? refreshErrorReason(e) : 'AUTH_REFRESH_4XX';
        clearWithBroadcast(reason);
        throw new ApiClientError(401, 'AUTH_TOKEN_INVALID', 'Session expired');
      }
    }
  }

  if (!res.ok) throw await parseApiError(res);
  return res;
}

export interface BinaryDownloadResult {
  blob: Blob;
  filename: string | null;
}

/**
 * @aiPurpose download a binary endpoint and return the body Blob plus the
 *   ASCII filename parsed from `Content-Disposition`. Errors are surfaced
 *   through the same `ApiClientError` / `NetworkError` channels as
 *   `request<T>` so caller toast/error patterns stay uniform.
 * @aiInvariant only ASCII `filename="..."` is parsed; RFC 5987
 *   `filename*` is intentionally NOT supported and the helper falls back
 *   to `null` so the caller can compute a deterministic fallback.
 */
export async function requestBlob(
  path: string,
  opts: RequestOptions = {},
  input: BinaryRequestInput = {},
): Promise<BinaryDownloadResult> {
  const res = await fetchBinaryWithAuthRetry(path, opts, input);
  const blob = await res.blob();
  const filename = parseAsciiAttachmentFilename(res.headers.get('content-disposition'));
  return { blob, filename };
}

function parseAsciiAttachmentFilename(headerValue: string | null): string | null {
  if (!headerValue) return null;
  const match = /filename="([^"]+)"/i.exec(headerValue);
  return match?.[1] ?? null;
}

// WHY: each method forwards the optional `schema` to `request<T>` as its 4th
// argument. The shared-hooks `ApiClient` interface uses a structural
// `ResponseParser<T>` (no zod runtime dep in shared-hooks); web's webApiClient
// narrows it to a `ZodType<T>` at this boundary because `request` accepts
// that. The structural type is a strict supertype of `ZodType` for the
// `parse` channel, so the cast at the boundary is a documented widening
// and stays inside this file. We call the correct `request` overload
// explicitly based on whether a schema was supplied — `request` has no
// optional-schema overload and adding one would mask the schema-present
// vs schema-absent return-type contracts.
export const webApiClient: ApiClient = {
  get: <T>(path: string, opts: RequestOptions = {}, schema?: ResponseParser<T>) =>
    schema
      ? request<T>(path, { method: 'GET' }, opts, schema as ZodType<T>)
      : request<T>(path, { method: 'GET' }, opts),
  post: <T>(path: string, body?: unknown, opts: RequestOptions = {}, schema?: ResponseParser<T>) =>
    schema
      ? request<T>(path, jsonInit('POST', body), opts, schema as ZodType<T>)
      : request<T>(path, jsonInit('POST', body), opts),
  patch: <T>(
    path: string,
    body?: unknown,
    opts: RequestOptions = {},
    schema?: ResponseParser<T>,
  ) =>
    schema
      ? request<T>(path, jsonInit('PATCH', body), opts, schema as ZodType<T>)
      : request<T>(path, jsonInit('PATCH', body), opts),
  put: <T>(path: string, body?: unknown, opts: RequestOptions = {}, schema?: ResponseParser<T>) =>
    schema
      ? request<T>(path, jsonInit('PUT', body), opts, schema as ZodType<T>)
      : request<T>(path, jsonInit('PUT', body), opts),
  delete: <T>(path: string, opts: RequestOptions = {}, schema?: ResponseParser<T>) =>
    schema
      ? request<T>(path, { method: 'DELETE' }, opts, schema as ZodType<T>)
      : request<T>(path, { method: 'DELETE' }, opts),
};
